import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import * as usersAPI from "../../api/user";
import {
  getDashboardErrorMessage,
  unwrapDashboardResponse,
} from "../../api/dashboardResponse";
import {
  createDashboardMutationThunk,
  createDashboardQueryThunk,
  createEntityListSlice,
  createEntityListStateUpdater,
} from "../utils/createEntityListSlice";

const updateUserListState = createEntityListStateUpdater("userList");

const getUserData = createDashboardQueryThunk(
  "users/getUserData",
  usersAPI.getUser,
  "Failed to load users"
);

const addUserData = createDashboardMutationThunk(
  "users/addUserData",
  usersAPI.addUser,
  {
    fallbackMessage: "Add failed",
    getSuccessMessage: "User Added Successfully",
  }
);

const searchUser = createDashboardQueryThunk(
  "users/searchUser",
  usersAPI.searchUser,
  "Search failed"
);

const updateUserData = createDashboardMutationThunk(
  "users/updateUserData",
  usersAPI.updateUser,
  {
    fallbackMessage: "Update failed",
    getSuccessMessage: (params) =>
      params?.status === "Active"
        ? "User Updated Successfully"
        : "User Deleted Successfully",
  }
);

const getRolesData = createAsyncThunk(
  "users/getUserRoles",
  async (_, { rejectWithValue }) => {
    try {
      const res = await usersAPI.getUsersRole();
      return unwrapDashboardResponse(res, "Failed to load roles");
    } catch (error) {
      const message = getDashboardErrorMessage(error, "Failed to load roles");
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

const addUserWithClientId = createDashboardMutationThunk(
  "users/addUserWithClientId",
  usersAPI.addUserWithClient,
  {
    fallbackMessage: "Add failed",
    getSuccessMessage: "User Added Successfully",
  }
);

const getUsersWithClientId = createDashboardQueryThunk(
  "users/getUsersWithClientId",
  usersAPI.getUsersForClient,
  "Failed to load users"
);

const searchUsersWithClientId = createDashboardQueryThunk(
  "users/searchUsersWithClientId",
  usersAPI.searchUsersWithClient,
  "Search failed"
);

const usersSlice = createEntityListSlice({
  name: "users",
  extraState: {
    userRoles: null,
  },
  asyncCases: [
    { thunk: getUserData },
    {
      thunk: getUsersWithClientId,
      options: {
        onRejected: (state) => {
          state.data = null;
        },
      },
    },
    { thunk: addUserData },
    {
      thunk: addUserWithClientId,
      options: {
        onRejected: (state) => {
          state.data = null;
        },
      },
    },
    { thunk: searchUser },
    {
      thunk: searchUsersWithClientId,
      options: {
        onRejected: (state) => {
          state.data = null;
        },
      },
    },
    {
      thunk: updateUserData,
      options: {
        onFulfilled: updateUserListState,
      },
    },
    {
      thunk: getRolesData,
      options: {
        loadingKey: null,
        onFulfilled: (state, action) => {
          state.userRoles = action.payload;
        },
        onRejected: (state) => {
          state.userRoles = [];
        },
      },
    },
  ],
});

const { resetState, setRowsPerPage, setPageNumber } = usersSlice.actions;

export default usersSlice.reducer;

export {
  getUserData,
  resetState,
  setPageNumber,
  setRowsPerPage,
  addUserData,
  updateUserData,
  searchUser,
  getRolesData,
  addUserWithClientId,
  getUsersWithClientId,
  searchUsersWithClientId,
};
